import React, { useEffect, useState } from 'react';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import {
  MaterialRequestRecord,
  MaterialRequestStatusForm,
  MRNextStatus,
} from '../../../../types/MaterialRequest';
import { Button, Card, Form, Input, Space, Timeline } from 'antd';
import { MaterialRequestStatusEnum } from '../../../../lib/enums';
import { isModuleApproved } from '../../../../lib/utils';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../../../../store';
import { useUpdateData, useUpdateStatus } from '../../../../hooks';
import { MRS_API } from '../../../../hooks/useMaterialRequestHooks';
import { StatusHistory } from '../../../../types';
import { TimelineItemProps } from 'antd/es/timeline/TimelineItem';
import TimelineView from '../../../../components/TimelineView';

interface MaterialRequestInfoStatusProps {
  data?: MaterialRequestRecord;
  statusHistory: StatusHistory[];
}
const MaterialRequestInfoStatus: React.FC<MaterialRequestInfoStatusProps> = ({
  data,
  statusHistory,
}) => {
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const params = useParams();

  const { mutate, isPending, isSuccess, isError, error } = useUpdateStatus(
    `${MRS_API}/${params.id}/status`,
  );

  const [cancelClicked, setCancelClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const userModuleAccessRole = user?.moduleAccess?.find(
    (item) => item.module.code === 'MR',
  );

  const onFinish = (values: any) => {
    const checkAndSetError = (
      field: keyof MaterialRequestRecord,
      errorMessage: string,
    ) => {
      if (data?.[field] === undefined || data?.[field] === null) {
        setIsErrorModalOpen(true);
        setErrorMess(errorMessage);
        return true; // Indicate that an error occurred
      }
      return false; // Indicate no error
    };

    // check if the required fields have values
    if (
      checkAndSetError('requiredDate', 'Please fill up required date') ||
      checkAndSetError('destination', 'Please fill up destination')
    ) {
      return; // Stop further processing if any error occurs
    }

    // Proceed with further processing if all required fields have values
    const form: MaterialRequestStatusForm = {
      status: MRNextStatus(data?.status),
      remarks: values.remarks,
    };
    mutate({
      id: data?.id,
      form: form,
    });
  };

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isError) {
      setIsErrorModalOpen(true);
      console.log(error);
      setErrorMess(error?.response?.data?.message);
    }
  }, [isSuccess, isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    // navigate('/stock-movement/material-request');
  };

  const handleCancelClick = () => {
    const textValue = form.getFieldValue('remarks');
    if (!textValue?.trim()) {
      setCancelClicked(true);
      setErrorMessage('Text is required');
      setTimeout(() => {
        setErrorMessage('');
      }, 2000);
    } else {
      setCancelClicked(false);
      const form: MaterialRequestStatusForm = {
        status: MaterialRequestStatusEnum.CANCEL,
        remarks: textValue,
      };
      mutate({
        id: data?.id,
        form: form,
      });
    }
  };

  const renderCancelButton = () => {
    const isAdmin = user?.role === 'ROLE_ADMIN';
    const canCancel =
      userModuleAccessRole?.cancel &&
      (data?.status === MaterialRequestStatusEnum.DRAFT ||
        data?.status === MaterialRequestStatusEnum.CONFIRM);

    if (isAdmin || canCancel) {
      return (
        <Button danger onClick={handleCancelClick} loading={isPending}>
          Cancel
        </Button>
      );
    }
    return null;
  };
  let apiTimelineItems: TimelineItemProps[] = [];

  if (statusHistory && statusHistory.length > 0) {
    apiTimelineItems = statusHistory
      .map((item) => ({
        key: item.id,
        children: <TimelineView item={item} />,
      }))
      .sort((a, b) => {
        return parseInt(b.key) - parseInt(a.key);
      });
  }
  const timelineItems = [
    {
      key: 'status',
      children: (
        <div className={'space-y-2'}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              name="remarks"
              rules={[
                { required: cancelClicked, message: 'Please input your text!' },
              ]}
              validateStatus={cancelClicked ? 'error' : undefined}
              className={cancelClicked ? 'ant-form-item-has-error' : ''}
              help={cancelClicked ? errorMessage : ''}
            >
              <Input.TextArea rows={4} placeholder="Input your text here" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isPending}>
                  {MRNextStatus(data?.status)}
                </Button>
                {renderCancelButton()}
              </Space>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    ...apiTimelineItems,
    {
      key: 'Created',
      children: (
        <div className={'space-y-2 border rounded p-2'}>
          <div>
            <span className={'font-semibold'}>{data?.code}</span> requested by{' '}
            <span className={'font-semibold'}>{data?.createdBy}</span> requested
            date <span className={'font-semibold'}>{data?.timeCreated}</span>.
          </div>
        </div>
      ),
    },
  ];

  const filteredTimelineItems = timelineItems.filter((item) => {
    if (item.key !== 'status') {
      return true; // Pass through items that are not related to status
    }

    const recStatus = data?.status;
    const userRole = user?.role;
    const userModuleAccess = user?.moduleAccess;

    // Function to check if the user is an approver or admin
    const isApproverOrAdmin = () => {
      return (
        userRole === 'ROLE_ADMIN' ||
        isModuleApproved(userModuleAccess, 'MaterialRequest')
      );
    };

    switch (recStatus) {
      case MaterialRequestStatusEnum.CANCEL:
      case MaterialRequestStatusEnum.DONE:
        return false; // Filter out CANCEL and DONE statuses

      case MaterialRequestStatusEnum.DRAFT:
        // Allow if user is admin or the destination matches user's default location
        return userRole === 'ROLE_ADMIN' || userModuleAccessRole?.confirm;

      case MaterialRequestStatusEnum.CONFIRM:
        // Allow if user is admin or destination matches default location and user is an approver
        return (
          (isApproverOrAdmin() || userModuleAccessRole?.approve) &&
          user?.username !== data?.createdBy
        );

      case MaterialRequestStatusEnum.APPROVED:
      case MaterialRequestStatusEnum.AVAILABLE:
        // Allow if destination matches default location and user is an approver or admin
        return (
          (isApproverOrAdmin() || userModuleAccessRole?.available) &&
          user?.username !== data?.createdBy
        );
      case MaterialRequestStatusEnum.ISSUED:
        return (
          (isApproverOrAdmin() || userModuleAccessRole?.issuannce) &&
          user?.username !== data?.createdBy
        );

      default:
        return false; // Filter out any other statuses
    }
  });

  return (
    <>
      <Card style={{ margin: '1rem' }} title={'Status Timeline'}>
        <Timeline reverse={false} items={filteredTimelineItems} />
      </Card>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Material request successfully set to ${data?.status}`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default MaterialRequestInfoStatus;
